import { type FieldValue } from 'firebase/firestore';

import { type FromSchema } from 'json-schema-to-ts';

import { type IncorporationStatus, IncorporationStatusEnum } from './incorporation';
import { CompanyInfoBodySchema, type IntakeFormDirector, PersonalInfoBodySchema } from './userInfo';

export enum OnboardingStepEnum {
  INTAKE_FORM = 'intakeForm',
  PROXY_CONTRACT = 'proxyContract',
  PONTO = 'ponto',
  MANDATE = 'mandate',
}

export type OnboardingStepType = 'volmachten' | 'formulier' | 'ponto' | 'mandate';

export const stepLookup: {
  [key in OnboardingStepType]: OnboardingStepEnum;
} = {
  volmachten: OnboardingStepEnum.PROXY_CONTRACT,
  formulier: OnboardingStepEnum.INTAKE_FORM,
  ponto: OnboardingStepEnum.PONTO,
  mandate: OnboardingStepEnum.MANDATE,
};

export type OnboardingStepComponentStructure = {
  title: string;
  content: React.ReactNode;
  component: React.FC;
};

export enum OnboardingStepStatusEnum {
  PENDING = 'pending',
  IN_PROGRESS = 'inProgress',
  COMPLETED = 'completed',
  NOT_APPLICABLE = 'notApplicable',
}

export enum OnboardingStatusEnum {
  IDLE = 'idle',
  IN_PROGRESS = 'inProgress',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

type OnboardingStatus = 'idle' | 'inProgress' | 'completed' | 'cancelled';

type OnboardingStepStatus = 'pending' | 'inProgress' | 'completed' | 'notApplicable' | 'retry';

export type OnboardingContextType = OnboardingInfo & {
  onboardingProgressPercentage: number;
  areAllUserStepsCompleted: boolean;
  isCompany: boolean;
  isCompanySetupVisible: boolean;
};

const transitionDataSchema = {
  type: 'object',
  properties: {
    wantsToHaveIntroductoryMeetingWithAccountant: {
      type: ['boolean', 'integer'],
      enum: [0, 1, true, false],
    },
    currentAccountancy: { type: 'string' },
    accountant: {
      type: 'object',
      properties: {
        name: { type: 'string' },
        email: { type: 'string' },
        accountantSoftware: { type: 'string' },
      },
      additionalProperties: false,
    },
    extraRemarks: { type: 'string' },
    acquisitionChannel: { type: 'string' },
  },
  additionalProperties: false,
} as const;

type TransitionData = FromSchema<typeof transitionDataSchema>;

export type OnboardingInfo = {
  transitionInfo?: TransitionData;
  // When status is completed, we can automatically change user role to dossier
  status?: OnboardingStatus; // When status is completed - user needs to be changed to dossier and notified
  lastUpdatedAt?: Date | null | FieldValue;
  startedAt?: Date | null | FieldValue;
  isVisible: Boolean; // Clarifies if Onboarding should still be shown to the user in the front-end

  intakeForm: {
    isStepCompleted: boolean;
    formStatus: OnboardingStepStatus;
    lastUpdatedAt?: Date | null | FieldValue;
    // This property is used to determine if user has completed the step for UI purposes
    hasUserCompledStep: boolean;
  };
  proxyContract: {
    isStepCompleted: boolean;
    contractStatus: OnboardingStepStatus;
    lastUpdatedAt?: Date | null | FieldValue;
    hasUserCompledStep: boolean;
  };
  mandate: {
    isStepCompleted: boolean;
    lastUpdatedAt?: Date | null | FieldValue;
    hasUserCompledStep: boolean;
  };
  ponto: {
    isStepCompleted: boolean;
    lastUpdatedAt?: Date | null | FieldValue;
    hasUserCompledStep: boolean;
  };
  activation: {
    isStepCompleted: boolean;
    exactStatus: OnboardingStepStatus;
    userDataFilledInStatus: OnboardingStepStatus;
    lastUpdatedAt?: Date | null | FieldValue;
  };
};

export type OnboardingIntakeForm = OnboardingInfo['intakeForm'];
export type OnboardingProxyContract = OnboardingInfo['proxyContract'];
export type OnboardingMandate = OnboardingInfo['mandate'];
export type OnboardingActivation = OnboardingInfo['activation'];
export type OnboardingPonto = OnboardingInfo['ponto'];
const stepSchema = {
  type: 'object',
  properties: {
    isStepCompleted: { type: 'boolean' },
    lastUpdatedAt: {
      oneOf: [{ type: 'string', format: 'date-time' }, { type: 'null' }],
    },
    hasUserCompletedStep: { type: 'boolean' },
  },
  additionalProperties: false,
} as const;

export const onboardingInfoSchema = {
  type: 'object',
  properties: {
    transitionInfo: { type: 'object' },
    status: { type: 'string' },
    lastUpdatedAt: {
      oneOf: [{ type: 'string', format: 'date-time' }, { type: 'null' }],
    },
    startedAt: {
      oneOf: [{ type: 'string', format: 'date-time' }, { type: 'null' }],
    },
    intakeForm: {
      ...stepSchema,
      properties: {
        ...stepSchema.properties,
        formStatus: { type: 'string' },
      },
    },
    proxyContract: {
      ...stepSchema,
      properties: {
        ...stepSchema.properties,
        contractStatus: { type: 'string' },
      },
    },
    mandate: {
      ...stepSchema,
    },
    ponto: {
      ...stepSchema,
    },
    activation: {
      ...stepSchema,
      properties: {
        ...stepSchema.properties,
        exactStatus: { type: 'string' },
        userDataFilledInStatus: { type: 'string' },
      },
    },
  },
  additionalProperties: false,
} as const;

export const intakeFormRequestBody = {
  type: 'object',
  properties: {
    personalInfo: PersonalInfoBodySchema,
    companyInfo: CompanyInfoBodySchema,
    transitionInfo: transitionDataSchema,
  },
  required: ['personalInfo', 'companyInfo', 'transitionInfo'],
  additionalProperties: false,
} as const;

export type IntakeFormRequestBody = FromSchema<typeof intakeFormRequestBody>;

// personalInfo.identityCard.idFront and personalInfo.identityCard.idBack object inside of IntakeFormRequestBody needs to be me modified to File | null and keep the other properties untouched
export type IntakeFormValues = FromSchema<typeof intakeFormRequestBody> & {
  personalInfo: {
    identityCard?: {
      idFront?: File | null;
      idBack?: File | null;
      front?: string;
      back?: string;
      idFrontAndBackFile?: File | null;
      frontAndBack?: string;
      isOneIdFile?: boolean;
    };
    dependants?: object | FieldValue;
  };
  companyInfo: {
    isOnlyDirector?: 0 | 1 | undefined;
    companyDirectors?: IntakeFormDirector[];
    articlesOfAssociationFile?: File | null;
    articlesOfAssociation?: string;
    nacebelCode?: {
      level1?: string;
      level2?: string;
      level3?: string;
      level4?: string;
      code?: string;
    };
  };
  incorporationInfo: {
    yearlyRevenue?: Number;
    ownInvestment?: Number;
    utilityCost?: Number;
    phoneAndInternetCost?: Number;
    computerEquipmentCost?: Number;
    needsCompanyCar?: boolean;
    companyCar?: {
      financeMethod?: String;
      amountPerMonth?: Number;
      type?: String;
      fuelType?: String;
      monthlyFuelCost?: Number;
      extraCosts?: String;
    };
    bureauType?: String;
    investmentType?: string[] | undefined;
    investmentCosts?: Number;
    insuranceType?: String[];
    netWageCost?: Number;
    benefitsInKind?: string[] | undefined;
    nettoAllowance?: string;
    wageInitiation?: string;
    otherCost?: string;
  };
};

export type IntakeFormFirestoreValues = FromSchema<typeof intakeFormRequestBody>;

// TODO FIX AS ANY BELLOW
export const DEFAULT_ONBOARDING_INFO: OnboardingContextType = {
  intakeForm: {
    isStepCompleted: false,
    formStatus: OnboardingStepStatusEnum.PENDING,
    hasUserCompledStep: false,
    lastUpdatedAt: null,
  },
  proxyContract: {
    contractStatus: OnboardingStepStatusEnum.PENDING,
    hasUserCompledStep: false,
    isStepCompleted: false,
    lastUpdatedAt: null,
  },
  status: OnboardingStatusEnum.IDLE,
  lastUpdatedAt: null,
  startedAt: null,
  mandate: {
    isStepCompleted: false,
    lastUpdatedAt: null,
    hasUserCompledStep: false,
  },
  ponto: {
    isStepCompleted: false,
    lastUpdatedAt: null,
    hasUserCompledStep: false,
  },
  activation: {
    isStepCompleted: false,
    exactStatus: OnboardingStepStatusEnum.PENDING,
    userDataFilledInStatus: OnboardingStepStatusEnum.PENDING,
    lastUpdatedAt: null,
  },
  isVisible: true,
  onboardingProgressPercentage: 0,
  areAllUserStepsCompleted: false,
  isCompany: false,
} as any;

enum OnboardingNotificationTriggersEnum {
  userHasFinishedPreboarding = 'userHasFinishedPreboarding',
  astroActivatedCodabox = 'astroActivatedCodabox',
  astroActivatedDossier = 'astroActivatedDossier',
}

type OnboardingAstroTrigger = keyof typeof OnboardingNotificationTriggersEnum;

export const onboardingNotificationTriggerRequestBody = {
  type: 'object',
  properties: {
    trigger: {
      type: 'string',
      enum: Object.values(OnboardingNotificationTriggersEnum) as OnboardingAstroTrigger[],
    },
    shouldSendNotification: { type: 'boolean' },
  },
  required: ['trigger'],
  additionalProperties: false,
} as const;

export type OnboardingNotificationTriggerRequestBody = FromSchema<
  typeof onboardingNotificationTriggerRequestBody
>;

// UI types for onboarding
export type OnboardingProgressComponentVersion = 'homepage' | 'overview';

// Generate initial onboardingInfo document
export const initialOnboardingInfoDocument = {
  type: 'object',
  properties: {
    incorporationStatus: {
      type: 'string',
      enum: Object.keys(IncorporationStatusEnum) as readonly IncorporationStatus[],
    },
  },
  required: ['incorporationStatus'],
  additionalProperties: false,
} as const;

export type InitialOnboardingInfoDocument = FromSchema<typeof initialOnboardingInfoDocument>;
